import React, { useState } from 'react';
import {
    Paper,
    Typography,
    Box,
    Button,
    IconButton,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Divider,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Switch,
    FormControlLabel,
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    PowerSettingsNew as PowerIcon,
    Bolt as BoltIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useToast } from '../toast/useToast';
import {
    useRelayKonfigByObjektQuery,
    useCreateRelayKonfigMutation,
    useUpdateRelayKonfigMutation,
    useDeleteRelayKonfigMutation,
    useSendRelayCommandMutation,
} from '../../api/JsdhNotifikaceService/service/SiaMutations';
import type {
    RelayKonfig,
    CreateRelayKonfigRequest,
    UpdateRelayKonfigRequest,
    RelayType,
    RelayCommand,
} from '../../api/JsdhNotifikaceService/generated';

interface RelayControlSectionProps {
    objektId: number;
}

const RELAY_TYPES: { value: RelayType; label: string }[] = [
    { value: 'BYPASS', label: 'Bypass zóny' },
    { value: 'SIREN', label: 'Siréna' },
    { value: 'LIGHT', label: 'Světlo' },
    { value: 'DOOR', label: 'Dveře' },
    { value: 'OTHER', label: 'Jiný' },
];

const DEFAULT_GPIO_PINS = [17, 27, 22, 23];

const RelayControlSection: React.FC<RelayControlSectionProps> = ({ objektId }) => {
    const { showToast } = useToast();
    const [showAddDialog, setShowAddDialog] = useState(false);
    const [editingRelay, setEditingRelay] = useState<RelayKonfig | null>(null);
    const [pulseDialogRelay, setPulseDialogRelay] = useState<RelayKonfig | null>(null);
    const [pulseDurationMs, setPulseDurationMs] = useState(500);
    const [confirmOnRelay, setConfirmOnRelay] = useState<RelayKonfig | null>(null);
    const [deleteConfirmRelay, setDeleteConfirmRelay] = useState<RelayKonfig | null>(null);

    const [newRelay, setNewRelay] = useState<CreateRelayKonfigRequest>({
        objektId,
        relayZoneId: 1,
        gpioPin: 17,
        nazev: '',
        typ: 'BYPASS',
        inverted: false,
    });

    const [editForm, setEditForm] = useState<UpdateRelayKonfigRequest>({});

    const { data: relays, isLoading } = useRelayKonfigByObjektQuery(objektId);
    const createMutation = useCreateRelayKonfigMutation();
    const updateMutation = useUpdateRelayKonfigMutation();
    const deleteMutation = useDeleteRelayKonfigMutation();
    const commandMutation = useSendRelayCommandMutation();

    const handleCreateRelay = () => {
        createMutation.mutate(
            { ...newRelay, objektId },
            {
                onSuccess: () => {
                    setShowAddDialog(false);
                    setNewRelay({
                        objektId,
                        relayZoneId: (relays?.length || 0) + 1,
                        gpioPin: getNextAvailablePin(),
                        nazev: '',
                        typ: 'BYPASS',
                        inverted: false,
                    });
                },
            }
        );
    };

    const handleUpdateRelay = () => {
        if (!editingRelay) return;
        updateMutation.mutate(
            { id: editingRelay.id, objektId, data: editForm },
            {
                onSuccess: () => {
                    setEditingRelay(null);
                    setEditForm({});
                },
            }
        );
    };

    const handleSendCommand = (relay: RelayKonfig, command: RelayCommand, durationMs?: number) => {
        commandMutation.mutate({
            objektId,
            relayZoneId: relay.relayZoneId,
            data: { command, durationMs },
        });
    };

    const handlePulseCommand = () => {
        if (!pulseDialogRelay) return;
        handleSendCommand(pulseDialogRelay, 'PULSE', pulseDurationMs);
        setPulseDialogRelay(null);
    };

    const handleConfirmOn = () => {
        if (!confirmOnRelay) return;
        commandMutation.mutate(
            {
                objektId,
                relayZoneId: confirmOnRelay.relayZoneId,
                data: { command: 'ON' },
            },
            { onSuccess: () => setConfirmOnRelay(null) }
        );
    };

    const handleConfirmDelete = () => {
        if (!deleteConfirmRelay) return;
        deleteMutation.mutate(
            { id: deleteConfirmRelay.id, objektId },
            { onSuccess: () => setDeleteConfirmRelay(null) }
        );
    };

    const getRelayLabel = (relay: RelayKonfig | null) =>
        relay ? relay.nazev || `Zóna ${relay.relayZoneId}` : '';

    const getNextAvailablePin = () => {
        const usedPins = relays?.map((r) => r.gpioPin) || [];
        return DEFAULT_GPIO_PINS.find((pin) => !usedPins.includes(pin)) || 17;
    };

    const getRelayTypeLabel = (type: RelayType) => {
        return RELAY_TYPES.find((t) => t.value === type)?.label || type;
    };

    const startEdit = (relay: RelayKonfig) => {
        setEditingRelay(relay);
        setEditForm({
            nazev: relay.nazev,
            typ: relay.typ,
            inverted: relay.inverted,
            aktivni: relay.aktivni,
        });
    };

    if (isLoading) {
        return (
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            </Paper>
        );
    }

    return (
        <>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BoltIcon color="primary" />
                        <Typography variant="h6">Relé výstupy Raspberry Pi</Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowAddDialog(true)}
                        size="small"
                    >
                        Přidat relé
                    </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Relé výstupy Raspberry Pi slouží k přemostění zón ústředny. Světla, vrata a zásuvky ovládané při poplachu najdete v záložce Zařízení.
                </Typography>

                {relays && relays.length === 0 ? (
                    <Alert severity="info">
                        Žádné relé výstupy. Kliknutím na „Přidat relé“ přidejte nový výstup.
                    </Alert>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Zóna</TableCell>
                                    <TableCell>Název</TableCell>
                                    <TableCell>GPIO</TableCell>
                                    <TableCell>Typ</TableCell>
                                    <TableCell>Stav</TableCell>
                                    <TableCell align="center">Ovládání</TableCell>
                                    <TableCell align="right">Akce</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {relays?.map((relay) => (
                                    <TableRow key={relay.id}>
                                        <TableCell>{relay.relayZoneId}</TableCell>
                                        <TableCell>{relay.nazev || '-'}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={`GPIO ${relay.gpioPin}`}
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{getRelayTypeLabel(relay.typ)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={relay.aktivni ? 'Aktivní' : 'Neaktivní'}
                                                variant="outlined"
                                            />
                                            {relay.inverted && (
                                                <Chip
                                                    size="small"
                                                    label="Invertováno"
                                                    variant="outlined"
                                                    sx={{ ml: 0.5 }}
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                <Tooltip title="Zapnout">
                                                    <IconButton
                                                        size="small"
                                                        color="success"
                                                        onClick={() => setConfirmOnRelay(relay)}
                                                        disabled={!relay.aktivni || commandMutation.isPending}
                                                    >
                                                        <PowerIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Vypnout">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleSendCommand(relay, 'OFF')}
                                                        disabled={!relay.aktivni || commandMutation.isPending}
                                                    >
                                                        <PowerIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Puls">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => setPulseDialogRelay(relay)}
                                                        disabled={!relay.aktivni || commandMutation.isPending}
                                                    >
                                                        <BoltIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => startEdit(relay)}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => setDeleteConfirmRelay(relay)}
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Dialog pro pridani noveho relay */}
            <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Přidat relé výstup</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Číslo zóny"
                            type="number"
                            value={newRelay.relayZoneId}
                            onChange={(e) =>
                                setNewRelay({ ...newRelay, relayZoneId: parseInt(e.target.value) || 1 })
                            }
                            fullWidth
                            helperText="Unikátní číslo zóny (1–4)"
                        />
                        <TextField
                            label="GPIO Pin"
                            type="number"
                            value={newRelay.gpioPin}
                            onChange={(e) =>
                                setNewRelay({ ...newRelay, gpioPin: parseInt(e.target.value) || 17 })
                            }
                            fullWidth
                            helperText="BCM číslo GPIO pinu (např. 17, 27, 22, 23)"
                        />
                        <TextField
                            label="Název"
                            value={newRelay.nazev || ''}
                            onChange={(e) =>
                                setNewRelay({ ...newRelay, nazev: e.target.value })
                            }
                            fullWidth
                            placeholder="např. Bypass zóna 1"
                        />
                        <TextField
                            select
                            label="Typ"
                            value={newRelay.typ}
                            onChange={(e) =>
                                setNewRelay({
                                    ...newRelay,
                                    typ: e.target.value as RelayType,
                                })
                            }
                            fullWidth
                        >
                            {RELAY_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={newRelay.inverted || false}
                                    onChange={(e) =>
                                        setNewRelay({ ...newRelay, inverted: e.target.checked })
                                    }
                                />
                            }
                            label="Invertovaná logika (NC relé)"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowAddDialog(false)}>Zrušit</Button>
                    <Button
                        onClick={handleCreateRelay}
                        variant="contained"
                        disabled={createMutation.isPending}
                    >
                        {createMutation.isPending ? <CircularProgress size={20} /> : 'Přidat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro editaci relay */}
            <Dialog open={!!editingRelay} onClose={() => setEditingRelay(null)} maxWidth="sm" fullWidth>
                <DialogTitle>Upravit relé</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Název"
                            value={editForm.nazev || ''}
                            onChange={(e) =>
                                setEditForm({ ...editForm, nazev: e.target.value })
                            }
                            fullWidth
                        />
                        <TextField
                            select
                            label="Typ"
                            value={editForm.typ || editingRelay?.typ || 'BYPASS'}
                            onChange={(e) =>
                                setEditForm({
                                    ...editForm,
                                    typ: e.target.value as RelayType,
                                })
                            }
                            fullWidth
                        >
                            {RELAY_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editForm.inverted ?? editingRelay?.inverted ?? false}
                                    onChange={(e) =>
                                        setEditForm({ ...editForm, inverted: e.target.checked })
                                    }
                                />
                            }
                            label="Invertovaná logika"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editForm.aktivni ?? editingRelay?.aktivni ?? true}
                                    onChange={(e) =>
                                        setEditForm({ ...editForm, aktivni: e.target.checked })
                                    }
                                />
                            }
                            label="Aktivní"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditingRelay(null)}>Zrušit</Button>
                    <Button
                        onClick={handleUpdateRelay}
                        variant="contained"
                        disabled={updateMutation.isPending}
                    >
                        {updateMutation.isPending ? <CircularProgress size={20} /> : 'Uložit'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog pro pulse */}
            <Dialog open={!!pulseDialogRelay} onClose={() => setPulseDialogRelay(null)}>
                <DialogTitle>Odeslat puls</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Relé: {pulseDialogRelay?.nazev || `Zóna ${pulseDialogRelay?.relayZoneId}`}
                    </Typography>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Opravdu odeslat puls? Relé se fyzicky sepne na zadanou dobu.
                    </Alert>
                    <TextField
                        label="Doba pulsu (ms)"
                        type="number"
                        value={pulseDurationMs}
                        onChange={(e) => setPulseDurationMs(parseInt(e.target.value) || 500)}
                        fullWidth
                        helperText="Typicky 500–1000 ms"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPulseDialogRelay(null)}>Zrušit</Button>
                    <Button
                        onClick={handlePulseCommand}
                        variant="contained"
                        disabled={commandMutation.isPending}
                    >
                        {commandMutation.isPending ? <CircularProgress size={20} /> : 'Odeslat'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog potvrzeni zapnuti rele */}
            <Dialog open={!!confirmOnRelay} onClose={() => setConfirmOnRelay(null)}>
                <DialogTitle>Zapnout relé?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu zapnout relé <strong>{getRelayLabel(confirmOnRelay)}</strong>? Fyzicky se sepne výstup.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOnRelay(null)}>Zrušit</Button>
                    <Button
                        onClick={handleConfirmOn}
                        variant="contained"
                        color="success"
                        disabled={commandMutation.isPending}
                    >
                        {commandMutation.isPending ? <CircularProgress size={20} /> : 'Zapnout'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog potvrzeni smazani rele */}
            <Dialog open={!!deleteConfirmRelay} onClose={() => setDeleteConfirmRelay(null)}>
                <DialogTitle>Smazat relé?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Opravdu smazat relé <strong>{getRelayLabel(deleteConfirmRelay)}</strong>? Přijdete o jeho konfiguraci.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmRelay(null)}>Zrušit</Button>
                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Smazat'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default RelayControlSection;
